import { ApolloLink } from '@apollo/client';
import * as Sentry from '@sentry/react';
import type { OperationDefinitionNode } from 'graphql';
import { Observable } from '@apollo/client/utilities';

type OperationInfo = {
  operationName: string;
  operationType?: string;
};

function getOperationInfo(operation: any): OperationInfo {
  const operationName = operation?.operationName || 'anonymous';
  const opDef = operation?.query?.definitions?.find(
    (d: any): d is OperationDefinitionNode => d.kind === 'OperationDefinition',
  );
  const operationType = opDef?.operation;
  return { operationName, operationType };
}

function enrichScope(scope: Sentry.Scope, info: OperationInfo) {
  scope.setTag('graphql.operation_name', info.operationName);
  if (info.operationType) scope.setTag('graphql.operation_type', info.operationType);
  scope.setContext('graphql', info);
}

function captureGraphQLError(info: OperationInfo, gqlErr: any) {
  // IMPORTANT: Sentry works best with real Error instances.
  // Apollo/GraphQL errors are often plain objects; wrap them.
  const wrappedError = new Error(gqlErr?.message || 'GraphQL error');
  wrappedError.name = 'GraphQLError';

  Sentry.withScope((scope) => {
    enrichScope(scope, info);
    scope.setContext('graphql', {
      ...info,
      path: gqlErr?.path,
      extensions: gqlErr?.extensions,
    });
    scope.setExtra('graphql.original_error', gqlErr);
    Sentry.captureException(wrappedError);
  });
}

function captureNetworkError(info: OperationInfo, networkError: unknown) {
  Sentry.withScope((scope) => {
    enrichScope(scope, info);
    Sentry.captureException(networkError);
  });
}

/**
 * Minimal Sentry GraphQL client instrumentation for Apollo.
 *
 * Sentry's docs show a generic "GraphQLClient" integration; in this codebase we
 * use Apollo, so we instrument via an ApolloLink.
 *
 * Goals:
 * - Add GraphQL operation context to Sentry events.
 * - Record GraphQL errors (errors[] and networkError) as Sentry events.
 * - Create a tracing span per operation when tracing is enabled.
 *
 * Note: we intentionally do NOT attach variables by default (often sensitive).
 */
export function createSentryGraphqlClientLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    if (!forward) {
      return null;
    }

    const info = getOperationInfo(operation);
    const description = `${info.operationType ?? 'operation'} ${info.operationName}`;

    // Breadcrumb provides useful request context even without tracing.
    Sentry.addBreadcrumb({
      category: 'graphql',
      type: 'http',
      message: description,
      data: {
        operationName: info.operationName,
        operationType: info.operationType,
      },
      level: 'info',
    });

    // Span for distributed tracing / performance.
    const parentSpan = Sentry.getActiveSpan?.();
    const span = parentSpan
      ? parentSpan.startChild({
          op: 'graphql.client',
          description,
        })
      : undefined;

    // `forward(operation)` returns an Apollo Observable, not a Promise.
    return new Observable((observer) => {
      const sub = forward(operation).subscribe({
        next: (result) => {
          const graphQLErrors = Array.isArray((result as any)?.errors) ? (result as any).errors : [];
          const hasGraphQLErrors = graphQLErrors.length > 0;

          if (hasGraphQLErrors) {
            graphQLErrors.forEach((gqlErr: any) => captureGraphQLError(info, gqlErr));
          }

          if (span) {
            span.setStatus(hasGraphQLErrors ? 'internal_error' : 'ok');
            span.finish();
          }

          observer.next(result);
        },
        error: (networkError: unknown) => {
          // Network / transport errors don't appear in result.errors.
          captureNetworkError(info, networkError);

          if (span) {
            span.setStatus('internal_error');
            span.finish();
          }

          observer.error(networkError);
        },
        complete: () => observer.complete(),
      });

      return () => sub.unsubscribe();
    });
  });
}


